import type { BaseQueryPolicy } from "@/state/api/createBaseQuery";
import { failureText } from "@/state/api/requestFailure";
import {
  appendPermissions,
  type ManagementPermission,
  replaceRecordPermissions,
} from "@/state/slices/authenticationSlice";
import { serverErrorNotification } from "@/state/slices/notificationSlice";

/**
 * Layout's behaviour around every request (GEN5-5269 ARCHITECTURE item 3):
 * the permissions a response states go into the auth slice, and an endpoint
 * that opts in to error toasts gets layout's concise one (D3). Moved from
 * `state/api/index.ts`, with one change in what is collected: the old code
 * passed `permissions` on as the response gave it, while `permissionList`
 * drops every entry without a string `permission` and wraps a lone
 * (non-array) value in a list, so only well-formed permissions reach the
 * auth slice (a wrapped lone record can then take the replace path).
 */

const isRecordLike = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The object types whose detail responses state every `<type>.*` permission
 * the user holds on the record (genfive-backend-commons'
 * `ReturnUserPermissionForObject`, keyed by the record's `id`), so the store
 * takes them in place of what it held: a revoked one goes (GEN5-5283).
 * Verified in engagement-service for touchpoints; others still only append.
 */
const RECORDS_STATING_PERMISSIONS = new Set(["touchpoint"]);

/** The record a detail response states its permissions for, if it is one. */
function recordStatingPermissions(
  response: unknown,
  permissions: unknown
): { objectType: string; objectId: string | number } | null {
  if (!isRecordLike(response) || !Array.isArray(permissions)) return null;
  const { objectType, data } = response;
  if (
    typeof objectType !== "string" ||
    !RECORDS_STATING_PERMISSIONS.has(objectType) ||
    !isRecordLike(data)
  ) {
    return null;
  }
  const { id } = data;
  return typeof id === "number" || typeof id === "string"
    ? { objectType, objectId: id }
    : null;
}

/**
 * Checks if the route is an artifact route. In this case, we consider endpoints that return permissions with objectId to be artifact routes.
 * Routes such as "auth/roles" also return permissions, but they don't have an objectId, so we don't consider them artifact routes.
 */
const isArtifactRoute = (route: string): boolean => !route.includes("auth");

/**
 * A permission as the backend states it. Only `permission` is checked: the
 * other fields are taken as sent, as they always were (some routes state
 * them null).
 */
const isPermission = (value: unknown): value is ManagementPermission =>
  isRecordLike(value) && typeof value["permission"] === "string";

/**
 * The permissions in a `permissions` field (absent: none): a lone value is
 * wrapped in a list, and an entry without a string `permission` is dropped.
 */
const permissionList = (permissions: unknown): ManagementPermission[] =>
  (Array.isArray(permissions) ? permissions : [permissions]).filter(
    isPermission
  );

/** The `permissions` an item of a list states. */
const permissionsOf = (item: unknown): ManagementPermission[] =>
  isRecordLike(item) ? permissionList(item["permissions"] ?? []) : [];

export const LAYOUT_POLICY: BaseQueryPolicy = {
  onResult: (result, url, api) => {
    if (!isArtifactRoute(url) || url.includes("engagementPlans")) return;
    const data = isRecordLike(result.data) ? result.data["data"] : undefined;
    if (Array.isArray(data)) {
      api.dispatch(
        appendPermissions({ permissions: data.flatMap(permissionsOf) })
      );
    } else if (isRecordLike(data)) {
      const permissions = permissionList(data["permissions"] ?? []);
      const statedFor = recordStatingPermissions(result.data, permissions);
      api.dispatch(
        statedFor
          ? replaceRecordPermissions({ ...statedFor, permissions })
          : appendPermissions({ permissions })
      );
    }
  },
  onError: (failure, api) => {
    api.dispatch(serverErrorNotification(failureText(failure, "concise")));
  },
};
